import { FC, useCallback, useState } from 'react';
import { BubbleChartSeries } from '../../types';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Bubble } from 'react-chartjs-2';
import { AxisTransformer, getDefaultChartOptions, getDefaultChartPlugins } from '../../utils';
import { ChartData, ChartOptions } from 'chart.js';
import { DEFAULT_COLORS } from '../../constants';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

interface BubbleChartProps {
  data: BubbleChartSeries[];
  chartId: string;
  xAxisLabel: string;
  yAxisLabel: string;
  xAxisTransformer?: AxisTransformer;
  yAxisTransformer?: AxisTransformer;
  renderForExport?: boolean;
}

const transformDataForChart = (data: BubbleChartSeries[]): ChartData<'bubble'> => {
  if (!data || data.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const series = data[0];
  const maxWeight = Math.max(...series.points.map((point) => Number(point.weight)));
  const minWeight = Math.min(...series.points.map((point) => Number(point.weight)));

  const labels = series.points.map((point) => point.x);
  const datasets = [
    {
      label: series.name || 'Bubble Data',
      data: series.points.map((point) => ({
        x: Number(point.x),
        y: Number(point.y),
        r: Math.max(5, ((Number(point.weight) - minWeight) / (maxWeight - minWeight)) * 20 + 5),
      })),
      backgroundColor: series.points.map(
        (_, index) => DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      ),
      borderColor: series.points.map((_, index) => DEFAULT_COLORS[index % DEFAULT_COLORS.length]),
      borderWidth: 1,
    },
  ];

  return {
    labels,
    datasets,
  };
};

const BubbleChart: FC<BubbleChartProps> = ({
  data,
  chartId,
  xAxisLabel,
  yAxisLabel,
  xAxisTransformer,
  yAxisTransformer,
  renderForExport,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  const chartData = transformDataForChart(data);
  const isVisible = useIntersectionObserver(chartNode);
  const minX = Math.min(...data[0].points.map((point) => Number(point.x)));

  const chartOptions = getDefaultChartOptions({
    xAxisLabel,
    showLegend: false,
    beginXAtValue: minX,
    xAxisTransformationFuntion: xAxisTransformer,
    yAxisTransformationFuntion: yAxisTransformer,
    tooltipConfigs: {
      bodyConfigs: [
        {
          type: 'text',
          text: yAxisLabel,
        },
        {
          type: 'text-value',
          key: 'y',
          transformer: yAxisTransformer,
        },
        {
          type: 'break',
        },
        {
          type: 'text',
          text: `${xAxisLabel} (Weight)`,
        },
        {
          type: 'text-value',
          key: 'r',
          transformer: xAxisTransformer,
        },
      ],
    },
  });

  const chartPlugins = getDefaultChartPlugins({
    xLabel: xAxisLabel,
    yLabel: yAxisLabel,
  });

  return (
    <Box
      id={`pvx-bubble-chart-container-${chartId}`}
      sx={{ height: isMobile ? '400px' : '620px', width: '100%' }}
      ref={chartRef}
    >
      {renderForExport ? (
        <Bubble
          data={chartData}
          options={chartOptions as ChartOptions<'bubble'>}
          plugins={chartPlugins}
        />
      ) : (
        isVisible && (
          <Bubble
            data={chartData}
            options={chartOptions as ChartOptions<'bubble'>}
            plugins={chartPlugins}
          />
        )
      )}
    </Box>
  );
};

export default BubbleChart;
